import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Switch,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Circle } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { MessageIcon, StreamsIcon } from "../../components/icons";

import { ChecklistUsageProps, usage } from "../../data/checklists";
import { tokens } from "../../theme";
import apiService from "../../service/Api";

type TaskItem = {
  id: number;
  name: string;
  description?: string | null;
  points?: number;
};

type Task = {
  id: number;
  title: string; // section name
  description: string;
  inputType: string;
  priority: "critical" | "important" | "optional";
  completion: number;
  items: TaskItem[];
};

type ChecklistProps = {
  id: number;
  title: string;
  role: string;
  type: number;
  application: "pre_stream" | "on_stream" | "post_stream";

  active: boolean;
  usage: number;
  compliance: number;
  lastUpdate: {
    date: string;
    updatedBy: string;
  };
  tasks: Task[];
};

interface ChecklistApiResponse {
  id: number;
  name: string;
  roles_name: string;
  checklist_type?: number;
  phase: string;
  active?: boolean;
  updated_at: string;
  sections: {
    id: number;
    name: string; // ✅ TASK TITLE
    description: string | null;
    list_items: {
      id: number;
      name: string;
      description: string | null;
    }[];
  }[];
}

const ViewChecklist: React.FC = () => {
  const [checklist, setChecklist] = useState<ChecklistProps | undefined>(
    undefined
  );
  const { id } = useParams<{ id: string }>();

  const getChecklistById = async (
    id: number
  ): Promise<ChecklistApiResponse> => {
    const response = await apiService.get(`/api/checklists/checklist/${id}/`);

    return response.data as ChecklistApiResponse;
  };

  useEffect(() => {
    const ChecklistItemTest = async () => {
      try {
        await apiService.get(`/api/checklists/checklist-item/${id}/`);
      } catch (error) {
        console.log(
          "Test endpoint hit (expected failure if backend not setup)",
          error
        );
      }
    };

    if (id) {
      ChecklistItemTest();
    }
  }, [id]);

  const getApplicationChipProps = (
    application: "pre_stream" | "on_stream" | "post_stream"
  ) => {
    switch (application) {
      case "pre_stream":
        return {
          label: "Pre Stream",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };

      case "on_stream":
        return {
          label: "On Stream",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };

      case "post_stream":
        return {
          label: "Post Stream",
          bgcolor: "#1E88E540",
          color: "#1E88E5",
        };

      default:
        return {
          label: "Unknown",
          bgcolor: "#BDBDBD40",
          color: "#BDBDBD",
        };
    }
  };

  const getPercentageProps = (val: number) => {
    if (val >= 80) {
      return "#4BA663";
    } else if (val < 80 && val >= 65) {
      return "#0000FF";
    } else if (val < 65 && val >= 50) {
      return "#F79E1B";
    } else if (val < 50) {
      return "#EB4335";
    }
  };

  const getPriorityChipProps = (priority: string) => {
    switch (priority.toLowerCase()) {
      case "critical":
        return {
          label: "Pre Shoot",
          bgcolor: "#EB433540",
          color: "#EB4335",
        };
      case "important":
        return {
          label: "Post Shoot",
          bgcolor: "#0000FF40",
          color: "#0000FF",
        };
      default:
        return {
          label: "Optional",
          bgcolor: "#BDBDBD40",
          color: "#000",
        };
    }
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const backgroundColor = colors.primary[800] || "#A9B2C3";

  useEffect(() => {
    if (!id) return;

    const fetchChecklist = async () => {
      try {
        const data = await getChecklistById(Number(id));

        console.log("SECTIONS RECEIVED:", data.sections);

        const formatted: ChecklistProps = {
          id: Number(data.id),
          title: data.name,
          role: data.roles_name,
          type: Number(data.checklist_type ?? 0),
          application: data.phase as "pre_stream" | "on_stream" | "post_stream",

          active: true,
          usage: data.sections?.length ?? 0,
          compliance: 0,
          lastUpdate: {
            date: data.updated_at,
            updatedBy: "System",
          },
          tasks: (data.sections ?? []).map((section) => ({
            id: section.id,
            title: section.name,
            description: section.description ?? "",
            inputType: "checklist",
            priority: "optional",
            completion: 0,
            items: section.list_items ?? [], // ✅ ADD THIS
          })),
        };

        console.log(" MAPPED CHECKLIST:", formatted);
        console.log(" MAPPED TASKS:", formatted.tasks);
        setChecklist(formatted); // ✅ no TS error now
      } catch (error) {
        console.error("Failed to fetch checklist:", error);
      }
    };

    fetchChecklist();
  }, [id]);

  console.log({ checklist });

  if (!id || !checklist) return null;

  const { date, updatedBy } = checklist.lastUpdate;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ minHeight: "100vh", position: "relative" }}>
        {/* Pilot Banner */}
        <Card
          sx={{
            mb: "16px",
            boxShadow: 2,
            height: "100%",
            position: "sticky",
            transition: "1s ease-in-out",
            zIndex: 99,
            top: -16,
          }}
        >
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Box display={"flex"} alignItems={"center"} gap={0.4}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {checklist.title}
                </Typography>
                <Chip
                  label={getApplicationChipProps(checklist.application).label}
                  size={"small"}
                  sx={{
                    color: getApplicationChipProps(checklist.application).color,
                    borderRadius: 1,
                    fontWeight: 600,
                    bgcolor: getApplicationChipProps(checklist.application)
                      .bgcolor,
                  }}
                />
              </Box>
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Button
                  variant="outlined"
                  startIcon={
                    <StreamsIcon color="inherit" sx={{ fontSize: 20 }} />
                  }
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                  }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    <MessageIcon color="inherit" sx={{ fontSize: 20 }} />
                  }
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                  }}
                >
                  Duplicate
                </Button>
                <Box display={"flex"} alignItems={"center"} gap={0.4}>
                  <Switch
                    checked={checklist.active}
                    // onChange={handleChange}
                    color={checklist.active ? "primary" : "default"}
                    slotProps={{ input: { "aria-label": "controlled" } }}
                  />
                  <Typography variant={"subtitle2"}>
                    {checklist.active ? "Deactivate" : "Activate"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box display={"flex"} alignItems={"center"} gap={1}>
              <Chip
                label={checklist.role}
                size={"small"}
                sx={{
                  color: "#000",
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: "#BDBDBD40",
                }}
              />
              <Chip
                label={checklist.active ? "Active" : "Inactive"}
                icon={<Circle color={"inherit"} sx={{ width: "8px" }} />}
                size={"small"}
                sx={{
                  color: checklist.active ? "#4BA663" : "#F79E1B",
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: checklist.active ? "#4BA66340" : "#F79E1B40",
                }}
              />
            </Box>

            <Typography sx={{ mt: 2 }} variant={"subtitle2"}>
              {`Last updated: ${format(
                new Date(date),
                "dd MMMM yyyy"
              )} by ${updatedBy}`}
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          {/* Checklist Tasks */}
          <Grid size={8}>
            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box
                  mb={2}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Task Items
                  </Typography>
                  <Box display={"flex"} alignItems={"center"} gap={0.4}>
                    <Typography variant="caption">
                      Checklist usage during the last 10 events
                    </Typography>
                    <Circle sx={{ fontSize: "6px" }} />
                    <Typography variant="caption">
                      Ordered by priority
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {checklist.tasks?.map((tsk) => {
                    return (
                      <Accordion
                        key={tsk.id}
                        defaultExpanded
                        sx={{
                          mb: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "none",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                bgcolor: "#BDBDBD40",
                                borderRadius: 1,
                                padding: 0.8,
                                color: "#000",
                              }}
                            >
                              <StreamsIcon
                                color="inherit"
                                sx={{ fontSize: 18 }}
                              />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {tsk.title}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.4}>
                                <Chip
                                  label={
                                    getPriorityChipProps(tsk.priority).label
                                  }
                                  size="small"
                                  sx={{
                                    color: getPriorityChipProps(tsk.priority)
                                      .color,
                                    borderRadius: 1,
                                    fontWeight: 600,
                                    bgcolor: getPriorityChipProps(tsk.priority)
                                      .bgcolor,
                                  }}
                                />
                                <Typography variant="caption">
                                  {tsk.inputType}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ pt: 0 }}>
                          <Box display="flex" flexDirection="column" gap={1}>
                            {tsk.items.map((item) => (
                              <Box
                                key={item.id}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 1,
                                  bgcolor: "#F7F7F7",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    bgcolor: "#EEEEEE",
                                  },
                                }}
                              >
                                {/* Left side */}
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: "text.secondary",
                                    }}
                                  />
                                  <Typography variant="body2" fontWeight={500}>
                                    {item.name}
                                  </Typography>
                                </Box>

                                {/* Right side – Points */}
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="text.secondary"
                                  sx={{
                                    minWidth: 48,
                                    textAlign: "right",
                                  }}
                                >
                                  {item.points ? `${item.points} pts` : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box
                  mb={2}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Usage
                  </Typography>
                  <Typography variant="caption">
                    Checklist usage during the last 10 events
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {/* Recent Checklist Usage Table Headings */}
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      mb: "4px",
                      bgcolor: backgroundColor,
                      padding: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Grid size={4} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Event
                      </Typography>
                    </Grid>
                    <Grid size={4} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Crew Member
                      </Typography>
                    </Grid>
                    <Grid size={2} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Date
                      </Typography>
                    </Grid>
                    <Grid size={2} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Completion
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Recent Checklist Usage Table Rows */}
                  <Box>
                    {usage &&
                      usage?.map((usg: ChecklistUsageProps, index: number) => {
                        const rowBg =
                          index % 2 !== 0 ? backgroundColor : "transparent";
                        return (
                          <Grid
                            key={usg.id}
                            container
                            spacing={2}
                            sx={{
                              my: 1,
                              bgcolor: rowBg,
                              padding: 1,
                              borderRadius: 1,
                            }}
                          >
                            <Grid size={4}>
                              <Box sx={{ lineHeight: 1 }}>
                                <Typography variant={"subtitle2"}>
                                  {usg.event.name}
                                </Typography>
                                <Typography variant={"caption"}>
                                  {usg.event.type}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid size={4}>
                              <Box
                                display={"flex"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Avatar
                                  src={""}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                  }}
                                />
                                <Box sx={{ lineHeight: 1 }}>
                                  <Typography variant={"subtitle2"}>
                                    {usg.pilot.name}
                                  </Typography>
                                  <Typography variant={"caption"}>
                                    {usg.pilot.id}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid size={2}>
                              <Box>
                                <Typography variant={"caption"}>
                                  {format(new Date(usg.date), "dd MMM yyyy")}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid size={2}>
                              <Typography
                                variant={"subtitle2"}
                                sx={{
                                  fontWeight: 600,
                                  color: getPercentageProps(usg.completion),
                                }}
                              >
                                {`${usg.completion}%`}
                              </Typography>
                            </Grid>
                          </Grid>
                        );
                      })}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Stats & Perfomance Insights */}
          <Grid size={4}>
            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Usage Statistics
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box
                    display={"flex"}
                    mb={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Times Used
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {checklist.usage}
                    </Typography>
                  </Box>

                  <Box
                    display={"flex"}
                    mb={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Compliance Avg.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getPercentageProps(checklist.compliance),
                        fontWeight: 600,
                      }}
                    >
                      {`${checklist.compliance}%`}
                    </Typography>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Most Skipped
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#EB4335" }}
                    >
                      47% completion
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 0 }}>
              <CardContent>
                <Box mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Perfomance Insights
                  </Typography>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      bgcolor: "#F79E1B10",
                      borderRadius: 2,
                      border: "1px solid",
                      padding: 1,
                      borderColor: "#F79E1B",
                      color: "#F79E1B",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Optional Tasks Skipped
                    </Typography>
                    <Typography variant="caption">
                      Consider making critical descriptions
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      bgcolor: "#4BA66310",
                      borderRadius: 2,
                      border: "1px solid",
                      padding: 1,
                      borderColor: "#4BA663",
                      color: "#4BA663",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      High Compliance
                    </Typography>
                    <Typography variant="caption">
                      Above average completion rate across all pilots
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 0 }}>
                  <Box
                    sx={{
                      bgcolor: "#0000FF10",
                      borderRadius: 2,
                      border: "1px solid",
                      padding: 1,
                      borderColor: "#0000FF",
                      color: "#0000FF",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      Frequently Used
                    </Typography>
                    <Typography variant="caption">
                      Checklist is popular among pixel pilots
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      {/* All Dialogs */}
    </motion.div>
  );
};

export default ViewChecklist;
